import type { VocabTrainingMetadataPayload } from "../networking/tests";

/**
 * What the best-effort sync actually did. It never gates the caller's return value — that one tracks
 * the pxp write only (see updateVocabTrainingSchoolClass) — it exists so the retry/fallback matrix is
 * assertable in tests.
 */
export enum VocabTrainingMetadataSyncOutcomes {
    UPSERTED = "UPSERTED",
    RECREATED = "RECREATED",
    NO_METADATA_ROW = "NO_METADATA_ROW",
    FAILED = "FAILED",
}

export interface SyncVocabTrainingSchoolClassMetadataParams {
    trainingId: string;
    // The class the training was just moved to in pxp. null = it was removed from its class.
    schoolClassId: string | null;
    // Backfill for rows written before teacherId existed: dropping it evicts the row from recent-articles.
    userId: string;
    // Last known row from the RTContext map. Used only on a removal, where a skipped write can never be
    // healed — see _resolveExistingMetadata for why a class move deliberately gets no fallback.
    cachedMetadata: VocabTrainingMetadataPayload | undefined;
    getVocabTrainingMetadata: (testUuid: string) => Promise<VocabTrainingMetadataPayload | undefined>;
    shareVocabTrainingMetadata: (payload: VocabTrainingMetadataPayload) => Promise<void>;
    recreateVocabTrainingMetadataWithoutSchoolClass: (payload: VocabTrainingMetadataPayload) => Promise<void>;
    // Called only for rows predating articleUuid, since it costs a trainings-list request. Whether the
    // row needs it is decided here; the caller only supplies the lookup.
    deriveArticleUuid: () => Promise<string | null>;
    // Same reasoning: resolving a missing shareLink costs a trainings-list request.
    resolveShareLink: () => Promise<string>;
    // Structurally typed rather than Dispatch<SetStateAction<...>> so this module imports no React.
    setVocabMetadata: (
        updater: (prev: Record<string, VocabTrainingMetadataPayload>) => Record<string, VocabTrainingMetadataPayload>
    ) => void;
    // Sentry stays with the caller so this module keeps no app-singleton import: the unit test needs no
    // module mock, and the caller is the one that knows which extras to attach.
    captureError: (error: unknown) => void;
}

/**
 * One immediate re-attempt, no backoff. The failures this path sees are overwhelmingly transient
 * (network blip, 5xx while the row is written from elsewhere), and a backing-off loop would outlive
 * the class change that triggered it — nothing is waiting on this result.
 */
const _retryOnce = async <T>(operation: () => Promise<T>): Promise<T> => {
    try {
        return await operation();
    } catch {
        return await operation();
    }
};

/**
 * Both injected lookups are optional enrichment with a defined fallback, so neither may sink the sync.
 * Guarded here rather than trusted: this function's "never throws" contract must not depend on an
 * invariant maintained in whichever file supplies the resolver.
 */
const _resolveOrFallback = async <T>(resolve: () => Promise<T>, fallback: T): Promise<T> => {
    try {
        return await resolve();
    } catch (e) {
        console.error("Optional vocab training metadata lookup failed, falling back", e);
        return fallback;
    }
};

interface ResolvedExistingMetadata {
    metadata: VocabTrainingMetadataPayload | undefined;
    // The read error itself, so the Sentry report carries the request's status and URL rather than a
    // synthetic stand-in. Undefined means the read succeeded — a definitive "no row", not a failure.
    readError?: unknown;
}

/**
 * The live row wins. On a removal the cached row is the fallback, because that branch is the one with
 * no second chance: the BE upsert ignores `schoolClassId: null`, so a skipped re-creation leaves a
 * schoolClass no later POST can ever clear (HALLOFUSS-6639).
 *
 * A class move deliberately gets NO fallback. There a stale schoolClassId is healed by the next share
 * or visibility edit, so the fallback would buy little while risking real harm: the cached row can be
 * older than the BE's (seeded by the load-time batch GET, or stale after another tab published the
 * training), and POSTing it wholesale would revert `visibilityDate` and re-hide a visible training.
 *
 * A successful read of `undefined` is a definitive "no row" — the cache must not resurrect one.
 */
const _resolveExistingMetadata = async (
    params: SyncVocabTrainingSchoolClassMetadataParams
): Promise<ResolvedExistingMetadata> => {
    try {
        return { metadata: await params.getVocabTrainingMetadata(params.trainingId) };
    } catch (e) {
        console.error("Failed to read vocab training metadata during schoolClass sync", e);
        return { metadata: params.schoolClassId === null ? params.cachedMetadata : undefined, readError: e };
    }
};

// Mirrors what was just written to the BE, so consumers stop decoding the previous schoolClass without
// waiting for a refetch.
const _cacheMetadata = (params: SyncVocabTrainingSchoolClassMetadataParams, metadata: VocabTrainingMetadataPayload) => {
    params.setVocabMetadata((prev) => ({ ...prev, [params.trainingId]: metadata }));
};

// The row's state is unknown after a failed re-creation (the delete leg may have gone through), and the
// training is off its class in pxp either way — so stop decoding the cached schoolClass locally.
const _forgetCachedMetadata = (params: SyncVocabTrainingSchoolClassMetadataParams) => {
    params.setVocabMetadata((prev) => {
        if (!(params.trainingId in prev)) return prev;
        const next = { ...prev };
        delete next[params.trainingId];
        return next;
    });
};

/**
 * Best-effort sync of a vocab training's metadata row after its pxp schoolClass changed. Never throws —
 * the caller's boolean tracks the pxp write only, so a metadata failure must not surface there. Both
 * writes are retried once; every failure that survives is reported through the injected captureError.
 * The read is deliberately not retried: on a removal it falls back to the cached row instead.
 */
export const syncVocabTrainingSchoolClassMetadata = async (
    params: SyncVocabTrainingSchoolClassMetadataParams
): Promise<VocabTrainingMetadataSyncOutcomes> => {
    const { metadata: existingMetadata, readError } = await _resolveExistingMetadata(params);

    if (!existingMetadata) {
        // Nothing was written, so on a removal the cached entry still decodes the class the training
        // just left — drop it whether the row was missing or merely unreadable.
        if (params.schoolClassId === null) _forgetCachedMetadata(params);

        if (readError !== undefined) {
            // Nothing to write from, so the row keeps its old schoolClass and nothing re-syncs it.
            params.captureError(readError);
            return VocabTrainingMetadataSyncOutcomes.FAILED;
        }
        // Legacy training without a row. Not an error: creating one here would fabricate the
        // testName/bookName/vocabularyCount we don't have.
        return VocabTrainingMetadataSyncOutcomes.NO_METADATA_ROW;
    }

    // Carried over: dropping either will evict the row from recent-articles. Rows predating the fields
    // carry neither, so both fall back rather than write undefined.
    //
    // `null` is a legitimate value (own-content trainings have no source book), so only a genuinely
    // absent field is re-derived — deriving would cost a trainings-list request and overwrite the null.
    const teacherId = existingMetadata.teacherId || params.userId;
    const articleUuid =
        existingMetadata.articleUuid !== undefined
            ? existingMetadata.articleUuid
            : await _resolveOrFallback(() => params.deriveArticleUuid(), null);

    if (params.schoolClassId === null) {
        // Re-created rather than just updated: the upsert ignores `schoolClassId: null`, so delete+insert
        // is the only FE-side way to clear it. The training keeps a metadata row on purpose — a row-less
        // training reads as legacy. Visibility was scoped to the removed schoolClass, so the fresh row
        // starts hidden.
        //
        // Rebuilt field by field so the BE's read-only extras aren't echoed back on write.
        const metadataWithoutSchoolClass: VocabTrainingMetadataPayload = {
            testUuid: params.trainingId,
            teacherId,
            articleUuid,
            // Carried over, not re-derived: that needs the subject info the sync doesn't have.
            json: existingMetadata.json,
            schoolClassId: null,
            testName: existingMetadata.testName,
            bookName: existingMetadata.bookName,
            vocabularyCount: existingMetadata.vocabularyCount,
            shareLink: existingMetadata.shareLink,
            visibilityDate: null,
        };

        try {
            await _retryOnce(() => params.recreateVocabTrainingMetadataWithoutSchoolClass(metadataWithoutSchoolClass));
            _cacheMetadata(params, metadataWithoutSchoolClass);
            return VocabTrainingMetadataSyncOutcomes.RECREATED;
        } catch (e) {
            console.error("Failed to re-create vocab training metadata without a schoolClass", e);
            params.captureError(e);
            _forgetCachedMetadata(params);
            return VocabTrainingMetadataSyncOutcomes.FAILED;
        }
    }

    // A non-null target goes through the plain upsert and stays there: only `schoolClassId: null` is
    // ignored by the BE, so delete+recreate would add no capability here — it would only trade a
    // recoverable failure (stale schoolClass, healed by the next POST) for a destructive one (a failed
    // re-insert after a successful delete loses the row entirely).
    //
    // The spread alone would re-post shareLink, teacherId and articleUuid as undefined for rows
    // registered before those fields existed.
    const metadataWithSchoolClass: VocabTrainingMetadataPayload = {
        ...existingMetadata,
        teacherId,
        articleUuid,
        schoolClassId: params.schoolClassId,
        // Resolved live only when the row predates shareLink; the lookup costs a trainings-list request.
        shareLink: existingMetadata.shareLink || (await _resolveOrFallback(params.resolveShareLink, "")),
    };

    try {
        await _retryOnce(() => params.shareVocabTrainingMetadata(metadataWithSchoolClass));
        _cacheMetadata(params, metadataWithSchoolClass);
        return VocabTrainingMetadataSyncOutcomes.UPSERTED;
    } catch (e) {
        console.error("Failed to sync vocab training metadata schoolClassId", e);
        params.captureError(e);
        // The cache is left alone: the existing row (stale schoolClass, correct visibility) is still the
        // best guess, and the next share or visibility edit re-POSTs the full row.
        return VocabTrainingMetadataSyncOutcomes.FAILED;
    }
};
